import { useState, useEffect, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Banknote, Coins } from "lucide-react";

const COLONES_BILLS = [20000, 10000, 5000, 2000, 1000];
const COLONES_COINS = [500, 100, 50, 25, 10, 5];
const DOLLARS_BILLS = [100, 50, 20, 10, 5, 1];
const DOLLARS_COINS = [1, 0.50, 0.25, 0.10, 0.05, 0.01];

export default function CashCounter({ currency, onTotalChange }) {
  const bills = currency === "colones" ? COLONES_BILLS : DOLLARS_BILLS;
  const coins = currency === "colones" ? COLONES_COINS : DOLLARS_COINS;
  const symbol = currency === "colones" ? "₡" : "$";
  
  // Refs para todos los inputs
  const inputRefs = useRef({});
  
  const [billCounts, setBillCounts] = useState(
    bills.reduce((acc, bill) => ({ ...acc, [bill]: 0 }), {})
  );
  
  const [coinCounts, setCoinCounts] = useState(
    coins.reduce((acc, coin) => ({ ...acc, [coin]: 0 }), {})
  );

  const calculateTotal = (counts, denominations) => {
    return denominations.reduce((total, denom) => {
      return total + (parseFloat(counts[denom]) || 0) * denom;
    }, 0);
  };

  const billsTotal = calculateTotal(billCounts, bills);
  const coinsTotal = calculateTotal(coinCounts, coins);
  const grandTotal = billsTotal + coinsTotal;

  useEffect(() => {
    if (onTotalChange) {
      onTotalChange(grandTotal);
    }
  }, [grandTotal]);

  const handleBillChange = (denomination, value) => {
    setBillCounts(prev => ({
      ...prev,
      [denomination]: parseInt(value) || 0
    }));
  };

  const handleCoinChange = (denomination, value) => {
    setCoinCounts(prev => ({
      ...prev,
      [denomination]: parseInt(value) || 0
    }));
  };

  // Manejar Enter para avanzar al siguiente campo
  const handleKeyDown = (e, currentIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const totalInputs = bills.length + coins.length;
      const nextIndex = currentIndex + 1;
      
      if (nextIndex < totalInputs) {
        // Buscar el siguiente input
        const nextRef = inputRefs.current[nextIndex];
        if (nextRef) {
          nextRef.focus();
          nextRef.select();
        }
      }
    }
  };

  const formatAmount = (amount) => {
    return `${symbol}${amount.toFixed(2)}`;
  };

  const step = (setter, denom, delta) => setter(prev => ({ ...prev, [denom]: Math.max(0, (parseInt(prev[denom]) || 0) + delta) }));

  const renderRow = (denom, counts, setter, onChange, index, kind) => (
    <div key={denom} className="flex items-center gap-2 md:gap-3">
      <Label className="w-20 md:w-24 text-sm font-medium shrink-0">
        {formatAmount(denom)}
      </Label>
      <button type="button" className="md:hidden h-11 w-11 shrink-0 rounded-lg border border-slate-300 bg-white text-xl font-bold active:scale-95 transition-transform" onClick={() => step(setter, denom, -1)} data-testid={`${kind}-${currency}-${denom}-minus`}>−</button>
      <Input
        ref={(el) => inputRefs.current[index] = el}
        data-testid={`${kind}-${currency}-${denom}`}
        type="number"
        inputMode="numeric"
        min="0"
        value={counts[denom]}
        onChange={(e) => onChange(denom, e.target.value)}
        onKeyDown={(e) => handleKeyDown(e, index)}
        onFocus={(e) => e.target.select()}
        className="w-14 h-11 text-lg md:w-20 md:h-8 md:text-base text-center shrink-0"
        placeholder="0"
      />
      <button type="button" className="md:hidden h-11 w-11 shrink-0 rounded-lg border border-slate-300 bg-white text-xl font-bold active:scale-95 transition-transform" onClick={() => step(setter, denom, 1)} data-testid={`${kind}-${currency}-${denom}-plus`}>+</button>
      <span className="text-xs text-slate-600 flex-1 text-right md:flex-none md:text-left md:w-28 truncate">
        = {formatAmount(denom * counts[denom])}
      </span>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Billetes */}
      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
            <Banknote className="w-4 h-4" />
            Billetes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 px-3 md:px-6">
          {bills.map((bill, index) => renderRow(bill, billCounts, setBillCounts, handleBillChange, index, "bill"))}
          <div className="pt-2 border-t border-blue-300 flex justify-between font-semibold text-blue-700">
            <span>Subtotal:</span>
            <span data-testid={`bills-total-${currency}`}>{formatAmount(billsTotal)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Monedas */}
      <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2 text-green-700">
            <Coins className="w-4 h-4" />
            Monedas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 px-3 md:px-6">
          {coins.map((coin, index) => renderRow(coin, coinCounts, setCoinCounts, handleCoinChange, bills.length + index, "coin"))}
          <div className="pt-2 border-t border-green-300 flex justify-between font-semibold text-green-700">
            <span>Subtotal:</span>
            <span data-testid={`coins-total-${currency}`}>{formatAmount(coinsTotal)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Total General */}
      <div className="p-4 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg text-white">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold">Total {currency === "colones" ? "Colones" : "Dólares"}:</span>
          <span className="text-2xl font-bold" data-testid={`grand-total-${currency}`}>
            {formatAmount(grandTotal)}
          </span>
        </div>
      </div>
    </div>
  );
}